import {
  createContext, forwardRef, useContext, useId,
  type HTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react'
import { ChevronDown } from 'lucide-react'

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  /** Sentence case, set in `--t-label`. Always present: a control with a
   *  placeholder instead of a label loses its name the moment you type. */
  label: string
  hint?: string
  /** Shown in place of the hint, and marks the control invalid. One message
   *  slot, because a field showing both at once is a field arguing with
   *  itself. */
  error?: string
  children: ReactNode
}

interface FieldWiring {
  id: string
  describedBy: string | undefined
  invalid: boolean
}

/** So `Field.Input` and friends inherit the label's `for`, the message's
 *  `aria-describedby` and the invalid state without the caller repeating any
 *  of it. Outside a `Field` they still render — the caller then owes an
 *  `aria-label`. */
const Wiring = createContext<FieldWiring | null>(null)

const FieldRoot = forwardRef<HTMLDivElement, FieldProps>(function Field(
  { label, hint, error, className = '', children, ...rest },
  ref,
) {
  const id = useId()
  const message = error ?? hint
  const messageId = `${id}-msg`
  return (
    <div ref={ref} className={`u-field ${className}`.trim()} {...rest}>
      <label className="u-field__label" htmlFor={id}>{label}</label>
      <Wiring.Provider value={{ id, describedBy: message ? messageId : undefined, invalid: !!error }}>
        {children}
      </Wiring.Provider>
      {message && (
        <p id={messageId} className={`u-field__msg${error ? ' is-error' : ''}`}>{message}</p>
      )}
    </div>
  )
})

export type InputProps = InputHTMLAttributes<HTMLInputElement>

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className = '', ...rest }, ref,
) {
  const w = useContext(Wiring)
  return (
    <input
      ref={ref}
      id={w?.id}
      aria-describedby={w?.describedBy}
      aria-invalid={w?.invalid || undefined}
      className={`u-input ${className}`.trim()}
      {...rest}
    />
  )
})

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className = '', rows = 4, ...rest }, ref,
) {
  const w = useContext(Wiring)
  return (
    <textarea
      ref={ref}
      id={w?.id}
      rows={rows}
      aria-describedby={w?.describedBy}
      aria-invalid={w?.invalid || undefined}
      className={`u-input u-textarea ${className}`.trim()}
      {...rest}
    />
  )
})

export interface SelectOption {
  value: string
  /** A string, not a node: these become `<option>`s, which hold text. */
  label: string
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange' | 'children'> {
  value: string | null
  onChange: (value: string) => void
  options: SelectOption[]
  /** Shown while nothing is chosen. */
  placeholder?: string
}

/**
 * The browser's `<select>`, with its closed state drawn by us.
 *
 * **This is the one place the brief's design was not followed, and the reason is
 * measured.** Base UI's Select — a listbox in a positioned popup, which is what
 * the brief asks for — costs **37.6 kB gzipped** in the main chunk, against a
 * 40 kB budget for all three libraries put together (rule 4). Vaul is 21.3 and
 * Base UI's Checkbox and ToggleGroup are 11.5; those three come to 70.4, so the
 * cap is unreachable with the popup in it and nothing else in the set is
 * optional. The interface the brief fixes — `Field.Select`, `value`, `onChange`,
 * `options` — is unchanged, so putting Base UI's back is one file and one
 * number: +37.6 kB, for the manager to decide.
 *
 * What is actually lost is less than it sounds. WP 2.1 sets `color-scheme` on
 * the document in both themes, which is exactly what tells the platform to draw
 * its own popup dark — the one thing a native select gets wrong when an app only
 * recolours itself. The closed control is a token-styled box like every other
 * input; the open list is the phone's, which on iOS is the wheel Doug already
 * has everywhere else.
 */
const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { value, onChange, options, placeholder = 'Choose', className = '', ...rest },
  ref,
) {
  const w = useContext(Wiring)
  return (
    <span className="u-selectwrap">
      <select
        ref={ref}
        id={w?.id}
        aria-describedby={w?.describedBy}
        aria-invalid={w?.invalid || undefined}
        className={`u-input u-select ${className}`.trim()}
        value={value ?? ''}
        onChange={e => onChange(e.currentTarget.value)}
        {...rest}
      >
        {/* Only while nothing is chosen, and never selectable: a placeholder
            that can be chosen is a value, and then the field has two ways of
            saying "empty". */}
        {value == null && <option value="" disabled>{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown className="u-select__icon" size={20} aria-hidden="true" />
    </span>
  )
})

/**
 * One export, three controls. `Input`, `Textarea` and `Select` hang off `Field`
 * rather than standing beside it in `index.ts`: they are the field's insides,
 * they are useless without its label, and the fourteen names stay fourteen.
 */
export const Field = Object.assign(FieldRoot, { Input, Textarea, Select })
